import { EventType } from "./constants/event-type-constants";
import { MarketingClientConstants } from "./constants/marketing-client-constants";
import { ErrorHandler } from "./utils/error-handler-first-rw";
import { DOMHelper } from "./helpers/dom-helper";
import { httpInstance } from "./http/http";
import { Capping } from "./marketing-client-capping";
import { MarketingClientRWBase } from "./marketing-client-rw-base";
import { Utilization } from "./marketing-client-utilization";
import { Checkbox } from "./utils/checkbox";
import { Spinner } from "./utils/spinner";
import { Utils } from "./utils/utils";
import { BigDataClickID } from "./constants/big-data-constants";
import { RwAccountErrorCode, RwErrorMessage, RwSelector } from "./constants/rw-object.constants";
import { LiveSession } from "./utils/livesession";

export enum LayerType {
    MAIN = "main",
    CONFIRMATION = "confirmation",
    RESEND = "resend"
}

export type SocialType = "social-media-google" | "social-media-fb" | "social-media-apple";

export class MarketingClientRWFirstPromotion extends MarketingClientRWBase {
    layers: { [key in LayerType]: HTMLElement };
    form: HTMLFormElement | null;
    email: HTMLInputElement | null;
    registrationAccountButton: HTMLElement | null;
    emailErrorHandler: ErrorHandler;
    registrationAccountError: HTMLElement | null;
    checkboxA: Checkbox;
    checkboxB: Checkbox;
    isMobile: boolean;
    agreementA: Agreement;
    agreementB: Agreement;
    spinnerCreateAccount: Spinner;
    spinnerResendEmail: Spinner;
    resendEmailButton: HTMLElement | null;
    resendEmailError: HTMLElement | null;
    resendEmailButtonInError: HTMLElement | null;
    resetButton: HTMLElement | null;
    headerDesktop: HTMLElement | null;
    headerMobile: HTMLElement | null;
    socialList: Array<Element> = [];
    socialWrapper: HTMLElement | null;
    emailWrapper: HTMLElement | null;
    socialLogout: HTMLElement | null;
    socialEmail: HTMLElement | null;
    socialType: SocialType | null = null;
    offerButton: HTMLElement | null;
    helpContact: HTMLElement | null;

    path = "/";
    idPrefixSelector = "rwFirstPromotion-";
    idSuffixForm = "form";
    idSuffixCheckboxA = "form-checkbox-A";
    idSuffixCheckboxB = "form-checkbox-B";
    classSuffix = {
        email: "_form-email",
        agreement: "_form-agreement",
        registrationAccountButton: "_form-button",
        registrationAccountError: "_form-create-account-error",
        emailInLayer: "_user-email",
        spinnerRegistrationAccount: "_form-spinner",
        spinnerResendEmail: "_email-confirmation-screen-spinner",
        headerDesktop: " .header-content-desktop",
        headerMobile: " .header-content-mobile",
        socialListIcon: "_form-social-list-icon",
        socialWrapper: "_form-social-wrapper",
        emailWrapper: "_form-email-wrapper",
        socialLogout: "_form-social_account-logout",
        socialEmail: "_form-social_account-email",
        helpContact: "_email-resend-screen_description-link",
    };
    messages = RwErrorMessage;
    agreementFormId = 54;
    locationLogin = "https://login.wyborcza.pl/";
    loginChanel = {
        "social-media-google": "GOOGLEPLUS",
        "social-media-fb": "FACEBOOK",
        "social-media-apple": "APPLEID",
        "WYBORCZA": "WYBORCZA"
    }
    offerButtonSelector = ".offer-button";

    constructor(
        public capping: Capping,
        public utilizationObject: Utilization,
        public classSection: string = RwSelector.rwFirstPromotionSelector
    ) {
        super(capping, utilizationObject, classSection);
    }

    init(): void {
        this.initialize();
        this.initializeForm();

        if (!this.section || !this.form || !this.email || !this.registrationAccountButton) {
            return;
        }

        this.initializeCheckbox();
        this.showSwimmer();
        this.showSection();
        this.getAgreementForm(this.agreementFormId);
        this.initializeHeader();
        this.initializeSpinner();
        this.initializeLayers();
        this.initializeResendEmail();
        this.initializeReset();
        this.initializeSocial();
        this.initializeOfferButton();
        this.initializeHelpContact();
        this.initializeLiveSession();
        this.bindEvents();
        this.bindSwimmerEvents();
        this.getUserEmail();
    }

    initializeForm(): void {
        this.form = DOMHelper.getElementById(`${this.idPrefixSelector}${this.idSuffixForm}`) as HTMLFormElement;

        if (!this.form) {
            return;
        }

        this.email = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.email}`, this.form) as HTMLInputElement;
        this.registrationAccountButton = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.registrationAccountButton}`, this.form) as HTMLElement;

        if (!this.email) {
            return;
        }

        this.emailErrorHandler = new ErrorHandler(this.email);
        this.registrationAccountError = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.registrationAccountError}`, this.form) as HTMLElement;
    }

    initializeHeader(): void {
        this.headerDesktop = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.headerDesktop}`) as HTMLElement;
        this.headerMobile = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.headerMobile}`) as HTMLElement;
    }

    initializeSpinner(): void {
        this.spinnerCreateAccount = new Spinner(`${this.classSection}${this.classSuffix.spinnerRegistrationAccount}`);
        this.spinnerResendEmail = new Spinner(`${this.classSection}${this.classSuffix.spinnerResendEmail}`);
    }

    initializeCheckbox(): void {
        this.checkboxA = new Checkbox(`${this.idPrefixSelector}${this.idSuffixCheckboxA}`);
        this.checkboxB = new Checkbox(`${this.idPrefixSelector}${this.idSuffixCheckboxB}`);
    }

    initializeLayers(): void {
        this.layers = {
            [LayerType.MAIN]: DOMHelper.getElementById(`${this.idPrefixSelector}${LayerType.MAIN}`) as HTMLElement,
            [LayerType.CONFIRMATION]: DOMHelper.getElementById(`${this.idPrefixSelector}${LayerType.CONFIRMATION}`) as HTMLElement,
            [LayerType.RESEND]: DOMHelper.getElementById(`${this.idPrefixSelector}${LayerType.RESEND}`) as HTMLElement
        }

        this.showLayer();
    }

    initializeResendEmail(): void {
        this.resendEmailButton = DOMHelper.querySelector(".button", this.layers.confirmation) as HTMLElement;
        this.resendEmailError = DOMHelper.querySelector(".resend-email-error", this.layers.confirmation) as HTMLElement;
        this.resendEmailButtonInError = DOMHelper.querySelector(".resend-button", this.layers.confirmation) as HTMLElement;
    }

    initializeReset(): void {
        this.resetButton = DOMHelper.querySelector(".reset-button", this.layers.resend) as HTMLElement;
    }

    initializeSocial(): void {
        this.socialList = DOMHelper.querySelectorAsArray(`${this.classSection}${this.classSuffix.socialListIcon}`);
        this.socialWrapper = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.socialWrapper}`) as HTMLElement;
        this.emailWrapper = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.emailWrapper}`) as HTMLElement;
        this.socialLogout = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.socialLogout}`) as HTMLElement;
        this.socialEmail = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.socialEmail}`) as HTMLElement;
    }

    initializeOfferButton(): void {
        if (this.section) {
            this.offerButton = DOMHelper.querySelector(this.offerButtonSelector, this.section) as HTMLElement;
        }
    }

    initializeHelpContact(): void {
        if (this.section) {
            this.helpContact = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.helpContact}`) as HTMLElement;
        }
    }

    initializeLiveSession() {
        if (!this.form) return;
        new LiveSession(this.form).init();
    }

    bindEvents(): void {
        this.bindEventsToRegistrationAccount();
        this.bindEventsToEmailInput();
        this.bindEventsToResendEmail();
        this.bindEventsToResendEmailOnError();
        this.bindEventsToReset();
        this.bindEventsToSocialLogin();
        this.bindEventsToSocialLogout();
        this.bindEventsToBuyAnOffer();
        this.bindEventsToHelpContact();
    }

    bindEventsToSocialLogin(): void {
        if (this.socialList.length) {
            this.socialList.forEach(element => {
                element.addEventListener(EventType.CLICK, () => {
                    this.socialType = element.getAttribute("data-social") as SocialType;
                    this.utilize(this.createClickID(BigDataClickID.login), true);
                    this.login();
                });
            });
        }
    }

    bindEventsToSocialLogout(): void {
        if (this.socialLogout) {
            this.socialLogout.addEventListener(EventType.CLICK, () => {
                this.utilize(this.createClickID(BigDataClickID.logout), true);
                this.logout();
            });
        }
    }

    login() {
        if (this.socialType) {
            const tmp: SocialType = this.socialType;

            window[this.socialType].login((email: string) => {
                this.socialType = tmp;

                this.onSuccessLogin(email);
                return;
            });

            this.clearSocialType();
        }
    }

    onSuccessLogin(email: string): void {
        if (email && this.socialType) {
            this.socialWrapper?.classList.add(this.socialType);
            this.setSocialEmail(email);
            this.hideEmailWrapper();
            this.showSocialWrapper();
        }
    }

    getUserEmail() {
        if (window["social-media-google"]) {
            window["social-media-google"].getUserEmail = () => {
                return window['gapi'].auth2.getAuthInstance().currentUser.get().getBasicProfile().getEmail();
            }
        }
    }

    logout(): void {
        if (this.socialType) {
            window[this.socialType].logout();
            this.clearEmail();
            this.hideSocialWrapper();
            this.showEmailWrapper();
            this.clearSocialType();
        }
    }

    setSocialEmail(socialEmail: string): void {
        if (this.socialEmail) {
            this.socialEmail.innerHTML = socialEmail;
        }

        if (this.email) {
            this.email.value = socialEmail;
        }
    }

    clearSocialType(): void {
        if (this.socialType) {
            this.socialWrapper?.classList.remove(this.socialType);
            this.socialType = null;
        }
    }

    bindEventsToRegistrationAccount(): void {
        if (this.registrationAccountButton) {
            this.registrationAccountButton.addEventListener(EventType.CLICK, (event) => {
                event.preventDefault();
                const validateCheckboxes = this.validateCheckboxes();

                if (this.validateEmail() && validateCheckboxes) {
                    this.hideRegistrationAccountError();
                    this.spinnerCreateAccount.show();
                    this.utilize(this.createClickID(BigDataClickID.createAccount));
                    this.registrationAccount();
                }
            });
        }
    }

    validateCheckboxes(): boolean {
        this.checkboxA.toggle();
        this.checkboxB.toggle();

        return this.checkboxA.isValidate() && this.checkboxB.isValidate();
    }

    bindEventsToEmailInput(): void {
        if (this.email) {
            this.email.addEventListener(EventType.CHANGE, this.validateEmail.bind(this));
            this.email.addEventListener(EventType.INPUT, () => {
                return this.email?.value.length ? this.emailErrorHandler.hide() : this.emailErrorHandler.show(this.messages.required);
            });
        }
    }

    bindEventsToResendEmail(): void {
        if (this.resendEmailButton) {
            this.resendEmailButton.addEventListener(EventType.CLICK, (event) => {
                event.preventDefault();
                this.spinnerResendEmail.show();
                this.utilize(BigDataClickID.resendEmail, true);
                this.resendEmail();
            })
        }
    }

    bindEventsToResendEmailOnError(): void {
        if (this.resendEmailButtonInError) {
            this.resendEmailButtonInError.addEventListener(EventType.CLICK, (event) => {
                event.preventDefault();
                this.spinnerResendEmail.show();
                this.utilize(BigDataClickID.resendEmail, true);
                this.resendEmail();
            })
        }
    }

    bindEventsToReset(): void {
        if (this.resetButton) {
            this.resetButton.addEventListener(EventType.CLICK, (event) => {
                event.preventDefault();
                this.utilize(BigDataClickID.startProcessAgain, true);
                this.reset();
            })
        }
    }

    bindEventsToBuyAnOffer(): void {
        if (this.offerButton) {
            this.offerButton.addEventListener(EventType.CLICK, () => {
                this.utilize(this.createClickID(BigDataClickID.purchaseSubscription), true)
            })
        }
    }

    bindEventsToHelpContact(): void {
        if (this.helpContact) {
            this.helpContact.addEventListener(EventType.CLICK, () => {
                this.utilize(BigDataClickID.helpContact, true)
            })
        }
    }

    clearEmail(): void {
        if (this.email) {
            this.email.value = "";
        }
    }

    reset(): void {
        this.clearEmail();
        this.hideSocialWrapper();
        this.showEmailWrapper();
        this.clearSocialType();

        this.checkboxA.reset();
        this.checkboxB.reset();

        this.hideRegistrationAccountError();
        this.resendEmailError?.classList.add(MarketingClientConstants.hiddenClassName);
        this.resendEmailButton?.classList.remove(MarketingClientConstants.hiddenClassName);

        this.showHeader();
        this.showLayer(LayerType.MAIN);
    }

    validateEmail(): boolean {
        const value = this.email ? this.email.value : "",
            emailIsValid = Utils.validateEmail(value);

        if (!value.length) {
            return this.emailErrorHandler.show(this.messages.required);
        } else if (!emailIsValid) {
            return this.emailErrorHandler.show(this.messages.banned);
        } else {
            return this.emailErrorHandler.hide();
        }
    }

    registrationAccount(): Promise<any> {
        const data = {
            email: this.email ? String(this.email.value).trim() : "",
            articleUrl: window.location.href,
            fromUrl: window.location.href,
            testAB: "PASS",
            disableRedirects: "true",
            agreementA: this.agreementA.idAgreement.toString(),
            agreementB: this.agreementB.idAgreement.toString(),
            loginChanel: this.loginChanel[this.socialType || "WYBORCZA"]
        };

        return httpInstance
            .post({
                url: `${this.path}regwall/account`,
                data: new URLSearchParams(data),
                withCredentials: true,
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "*/*"
                }
            })
            .then(this.successRegistrationAccount.bind(this), this.errorRegistrationAccount.bind(this))
            .then(() => {
                this.spinnerCreateAccount.hide();
            });
    }

    successRegistrationAccount(response: any) {
        if (response.data && !response.data.error) {
            this.hideHeader();
            this.setEmailInLayer();
            this.sendBigDataDisplay(BigDataClickID.confirmationEmailAddress);
            this.showLayer(LayerType.CONFIRMATION);
            this.scrollToElement();
        }
    }

    errorRegistrationAccount(error: any) {
        const data = error.data,
            message = error.data.message,
            errorHasMessage = data && message;

        if (errorHasMessage && message === RwAccountErrorCode.accountExists) {
            this.emailErrorHandler.show(this.messages.accountExists);
            this.goToLogin();
        } else if (errorHasMessage && message === RwAccountErrorCode.accountUnconfirmed) {
            this.bindEventsToResendEmailOnAccountUnconfirmed();
        } else {
            this.showRegistrationAccountError();
        }
    }

    bindEventsToResendEmailOnAccountUnconfirmed(): void {
        this.emailErrorHandler.show(this.messages.accountUnconfirmed);

        if (this.emailErrorHandler.errorElement) {
            const tagA = DOMHelper.querySelector("a", this.emailErrorHandler.errorElement);

            tagA?.addEventListener(EventType.CLICK, (event) => {
                event.preventDefault();
                this.hideHeader();
                this.setEmailInLayer();
                this.resendEmail();
            })
        }
    }

    goToLogin(): void {
        window.location.href = this.locationLogin;
    }

    resendEmail(): Promise<any> {
        const data = {
            email: this.email ? String(this.email.value).trim() : ""
        };

        return httpInstance
            .post({
                url: `${this.path}regwall/resendConfEmailForRegWall`,
                data: new URLSearchParams(data),
                withCredentials: true,
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "*/*"
                }
            })
            .then(this.successResendEmail.bind(this), this.errorResendEmail.bind(this))
            .then(() => {
                this.spinnerResendEmail.hide();
            });
    }

    successResendEmail() {
        this.sendBigDataDisplay(BigDataClickID.checkMailbox);
        this.showLayer(LayerType.RESEND);
    }

    errorResendEmail() {
        this.hideResendEmailButton();
        this.showResendEmailError();
    }

    getAgreementForm(formId: number): Promise<any> {
        return httpInstance
            .get({
                url: `${this.squidHomeUrl}squid-home/open/accounts/agreements?formId=${formId}`
            })
            .then(this.successResponseAgreementForm.bind(this))
    }

    successResponseAgreementForm(response: any) {
        if (response.data && response.data.agreements && response.data.agreements.length === 2) {
            this.agreementA = response.data.agreements[0];
            this.agreementB = response.data.agreements[1];

            this.setAgreementText();
            // this.showSwimmer();
            // this.showSection();
        } else {
            console.warn("Incorrect number of agreements!");
            this.hideSwimmer();
            this.hideSection();
        }
    }

    setEmailInLayer(): void {
        const emailInConfirmation = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.emailInLayer}`, this.layers.confirmation),
            emailInResend = DOMHelper.querySelector(`${this.classSection}${this.classSuffix.emailInLayer}`, this.layers.resend),
            email = this.email ? this.email.value : "";

        if (emailInConfirmation) {
            emailInConfirmation.innerHTML = email;
        }

        if (emailInResend) {
            emailInResend.innerHTML = email;
        }
    }

    createClickID(prefixOfId: string): string {
        let suffixOfId: string;

        switch (this.socialType) {
            case "social-media-google":
                suffixOfId = " Google";
                break;
            case "social-media-fb":
                suffixOfId = " FB";
                break;
            case "social-media-apple":
                suffixOfId = " Apple";
                break;
            default:
                suffixOfId = "";
        }

        return prefixOfId + suffixOfId;
    }


    setAgreementText(): void {
        this.checkboxA.setText(this.agreementA.agreementText);
        this.checkboxB.setText(this.agreementB.agreementText);
    }

    showLayer(layer = LayerType.MAIN) {
        Object.values(this.layers).forEach(layer => {
            layer.classList.add(MarketingClientConstants.hiddenClassName);
        })

        this.layers[layer].classList.remove(MarketingClientConstants.hiddenClassName);
    }

    hideHeader(): void {
        this.headerDesktop?.classList.add(MarketingClientConstants.hiddenClassName);
        this.headerMobile?.classList.add(MarketingClientConstants.hiddenClassName);
    }

    showHeader(): void {
        this.headerDesktop?.classList.remove(MarketingClientConstants.hiddenClassName);
        this.headerMobile?.classList.remove(MarketingClientConstants.hiddenClassName);
    }

    showRegistrationAccountError(): void {
        this.registrationAccountError?.classList.remove(MarketingClientConstants.hiddenClassName);
    }

    hideRegistrationAccountError(): void {
        this.registrationAccountError?.classList.add(MarketingClientConstants.hiddenClassName);
    }

    showResendEmailButton(): void {
        this.resendEmailButton?.classList.remove(MarketingClientConstants.hiddenClassName);
    }

    hideResendEmailButton(): void {
        this.resendEmailButton?.classList.add(MarketingClientConstants.hiddenClassName);
    }

    showResendEmailError(): void {
        this.resendEmailError?.classList.remove(MarketingClientConstants.hiddenClassName);
    }

    hideResendEmailError(): void {
        this.resendEmailError?.classList.add(MarketingClientConstants.hiddenClassName);
    }

    showEmailWrapper(): void {
        this.emailWrapper?.classList.remove(MarketingClientConstants.hiddenClassName);
    }

    hideEmailWrapper(): void {
        this.emailWrapper?.classList.add(MarketingClientConstants.hiddenClassName);
    }

    showSocialWrapper(): void {
        this.socialWrapper?.classList.remove(MarketingClientConstants.hiddenClassName);
    }

    hideSocialWrapper(): void {
        this.socialWrapper?.classList.add(MarketingClientConstants.hiddenClassName);
    }
};

interface Agreement {
    idAgreement: number,
    agreementText: string,
    agreementName: string,
    optional: boolean,
    active: boolean,
    sorting: number,
    marketingAgreement: boolean
}
